import React, { useContext, useEffect, useMemo, useState } from "react";
import {
	Bar,
	BarChart,
	CartesianGrid,
	Cell,
	Legend,
	Line,
	LineChart,
	Pie,
	PieChart,
	ResponsiveContainer,
	Tooltip,
	XAxis,
	YAxis,
} from "recharts";

import AppPageTitle from "../../components/AppPageTitle";
import { ThemeContext } from "../../context/Theme/ThemeContext";
import api from "../../services/api";
import apiV2 from "../../services/apiV2";
import toastError from "../../errors/toastError";

const OFFICIAL = new Set([
	"whatsapp_business_account",
	"whatsappcloud",
	"whatsappbusiness",
]);

const CATEGORY_LABEL = {
	SERVICE: "Serviço",
	UTILITY: "Utilidade",
	MARKETING: "Marketing",
	AUTHENTICATION: "Autenticação",
	OTHER: "Outros",
};

const CATEGORY_HELP = [
	{
		key: "MARKETING",
		text: "Promoção, oferta, novidade e reativação. A Meta cobra cada mensagem entregue.",
	},
	{
		key: "UTILITY",
		text: "Aviso ligado a algo que o cliente já pediu: confirmação, lembrete, status de pedido.",
	},
	{
		key: "AUTHENTICATION",
		text: "Código de acesso, login e verificação. A Meta cobra cada mensagem entregue.",
	},
	{
		key: "SERVICE",
		text: "Resposta livre dentro da janela de atendimento. Cada número tem 1.000 mensagens grátis no mês; a partir da seguinte a Meta cobra.",
	},
];

const DONUT_COLORS = ["#0284c7", "#16a34a", "#d97706", "#7c3aed", "#db2777", "#0f766e", "#64748b", "#ea580c"];

const CATEGORY_COLOR = {
	SERVICE: "#64748b",
	UTILITY: "#0284c7",
	MARKETING: "#d97706",
	AUTHENTICATION: "#7c3aed",
};

function pad(n) {
	return String(n).padStart(2, "0");
}

function toDateInput(date) {
	return `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}`;
}

function monthStart() {
	const now = new Date();
	return toDateInput(new Date(now.getFullYear(), now.getMonth(), 1));
}

function labelOf(category) {
	return CATEGORY_LABEL[category] || category;
}

function formatInt(value) {
	return Number(value || 0).toLocaleString("pt-BR");
}

function formatBrl(value) {
	return Number(value || 0).toLocaleString("pt-BR", {
		style: "currency",
		currency: "BRL",
	});
}

function ChartTooltip({ active, payload, label, darkMode }) {
	if (!active || !payload?.length) return null;
	return (
		<div
			className="rounded-lg px-3 py-2 text-xs shadow-md"
			style={{
				background: darkMode ? "#18181b" : "#ffffff",
				border: `1px solid ${darkMode ? "#3f3f46" : "#e4e4e7"}`,
				color: darkMode ? "#fafafa" : "#18181b",
			}}
		>
			<p className="m-0 mb-1 font-semibold">{label}</p>
			{payload.map((item) => (
				<p key={item.dataKey} className="m-0" style={{ color: item.color || (darkMode ? "#e4e4e7" : "#3f3f46") }}>
					{item.name}: {formatInt(item.value)}
				</p>
			))}
		</div>
	);
}

function formatPct(value) {
	return `${(Number(value || 0) * 100).toLocaleString("pt-BR", {
		maximumFractionDigits: 1,
	})}%`;
}

function SectionIntro({ title, text }) {
	return (
		<div>
			<h2 className="m-0 text-base font-semibold text-default-800 dark:text-default-100">{title}</h2>
			<p className="m-0 mt-1 text-sm leading-6 text-default-500">{text}</p>
		</div>
	);
}

function adOrigin(item) {
	const app = String(item.sourceApp || "").toLowerCase();
	if (app.includes("instagram")) return "Instagram";
	if (app.includes("facebook")) return "Facebook";
	const kind = String(item.sourceType || "").toLowerCase();
	if (kind === "ad") return "Anúncio";
	if (kind === "post") return "Publicação";
	return item.sourceType || "Meta";
}

function Bone({ className, pulse }) {
	return (
		<div
			className={`rounded-lg bg-default-200 dark:bg-default-700 ${pulse ? "animate-pulse" : ""} ${className}`}
		/>
	);
}

function MetricsSkeleton({ pulse = false }) {
	return (
		<div className="space-y-5" aria-hidden="true">
			<Bone pulse={pulse} className="h-16 w-full" />
			<div className="grid gap-3 sm:grid-cols-2 xl:grid-cols-4">
				<Bone pulse={pulse} className="h-24" />
				<Bone pulse={pulse} className="h-24" />
				<Bone pulse={pulse} className="h-24" />
				<Bone pulse={pulse} className="h-24" />
			</div>
			<div className="card">
				<div className="card-body space-y-4">
					<Bone pulse={pulse} className="h-5 w-40" />
					<Bone pulse={pulse} className="h-4 w-full max-w-xl" />
					<Bone pulse={pulse} className="h-72 w-full" />
					<div className="grid gap-2 sm:grid-cols-2">
						<Bone pulse={pulse} className="h-12" />
						<Bone pulse={pulse} className="h-12" />
						<Bone pulse={pulse} className="h-12" />
						<Bone pulse={pulse} className="h-12" />
					</div>
				</div>
			</div>
			<div className="card">
				<div className="card-body space-y-3">
					<Bone pulse={pulse} className="h-5 w-64" />
					<Bone pulse={pulse} className="h-4 w-full max-w-lg" />
					<Bone pulse={pulse} className="h-36 w-full" />
				</div>
			</div>
			<div className="grid gap-5 lg:grid-cols-2">
				<div className="card">
					<div className="card-body space-y-3">
						<Bone pulse={pulse} className="h-5 w-52" />
						<Bone pulse={pulse} className="h-4 w-full" />
						<Bone pulse={pulse} className="mx-auto h-52 w-52 rounded-full" />
					</div>
				</div>
				<div className="card">
					<div className="card-body space-y-3">
						<Bone pulse={pulse} className="h-5 w-44" />
						<Bone pulse={pulse} className="h-4 w-full" />
						<Bone pulse={pulse} className="h-52 w-full" />
					</div>
				</div>
			</div>
		</div>
	);
}

function Stat({ label, value, hint }) {
	return (
		<div className="rounded-xl border border-default-200 bg-default-50/80 px-4 py-3 dark:border-default-700 dark:bg-default-950/40">
			<p className="m-0 text-[11px] font-semibold uppercase tracking-wide text-default-500">
				{label}
			</p>
			<p className="m-0 mt-2 text-2xl font-bold text-default-900 dark:text-default-50">
				{value}
			</p>
			{hint ? <p className="m-0 mt-1 text-xs text-default-500">{hint}</p> : null}
		</div>
	);
}

export default function WhatsAppMetrics() {
	const { darkMode } = useContext(ThemeContext);
	const axisColor = darkMode ? "#e4e4e7" : "#3f3f46";
	const gridColor = darkMode ? "#3f3f46" : "#e4e4e7";
	const [connections, setConnections] = useState([]);
	const [connectionId, setConnectionId] = useState("");
	const [start, setStart] = useState(monthStart);
	const [end, setEnd] = useState(() => toDateInput(new Date()));
	const [loading, setLoading] = useState(false);
	const [report, setReport] = useState(null);

	useEffect(() => {
		let cancelled = false;
		api
			.get("/whatsapp/")
			.then(({ data }) => {
				if (cancelled) return;
				const list = (Array.isArray(data) ? data : []).filter((item) =>
					OFFICIAL.has(item.channel)
				);
				setConnections(list);
			})
			.catch((err) => toastError(err));
		return () => {
			cancelled = true;
		};
	}, []);

	const search = async () => {
		if (loading || !connectionId) return;
		setLoading(true);
		try {
			const payload = {
				start,
				end,
				connectionId: Number(connectionId),
			};
			const { data } = await apiV2.post("/wa-metrics", payload);
			setReport(data);
		} catch (err) {
			toastError(err);
		} finally {
			setLoading(false);
		}
	};

	const volume = report?.volume || {};
	const stats = volume.stats || {};
	const categories = Array.isArray(volume.categories) ? volume.categories : [];
	const series = Array.isArray(volume.series) ? volume.series : [];
	const prices = Array.isArray(report?.pricing?.categories)
		? report.pricing.categories
		: [];
	const templates = Array.isArray(report?.templates) ? report.templates : [];
	const ads = Array.isArray(report?.ads) ? report.ads : [];
	const channels = Array.isArray(report?.channels) ? report.channels : [];

	const priceTotal = useMemo(
		() => prices.reduce((sum, item) => sum + Number(item.totalBrl || 0), 0),
		[prices]
	);

	const templateDonut = templates
		.filter((item) => Number(item.replied) > 0)
		.map((item) => ({
			name: item.name || item.templateId || "Template",
			value: Number(item.replied) || 0,
		}));

	const adsChart = ads.slice(0, 8).map((item) => ({
		name: item.title || item.sourceId || "Anúncio",
		Cliques: Number(item.clicks) || 0,
		Pessoas: Number(item.contacts) || 0,
	}));
	const adClicks = ads.reduce((sum, item) => sum + Number(item.clicks || 0), 0);
	const adPeople = ads.reduce((sum, item) => sum + Number(item.contacts || 0), 0);

	return (
		<div className="app-admin-page">
			<AppPageTitle
				title="Métricas do WhatsApp oficial"
				subtitle="Quanto a Meta entregou, quanto isso representa em reais, o que o envio em massa respondeu e quais anúncios abriram conversa."
			/>

			<div className="app-admin-page__scroll">
				<div className="space-y-5">
					<div className="card">
						<div className="card-body space-y-4">
							<SectionIntro
								title="Período e canal"
								text="Escolha um canal oficial. A consulta vale só para esse número. O intervalo máximo é de 90 dias."
							/>
							<div className="grid gap-4 md:grid-cols-4">
								<label className="space-y-2 text-sm font-medium text-default-700 dark:text-default-200">
									Início
									<input
										type="date"
										className="form-input w-full"
										value={start}
										onChange={(e) => setStart(e.target.value)}
									/>
								</label>
								<label className="space-y-2 text-sm font-medium text-default-700 dark:text-default-200">
									Fim
									<input
										type="date"
										className="form-input w-full"
										value={end}
										onChange={(e) => setEnd(e.target.value)}
									/>
								</label>
								<label className="space-y-2 text-sm font-medium text-default-700 dark:text-default-200 md:col-span-2">
									Canal oficial
									<select
										className="form-input w-full"
										value={connectionId}
										onChange={(e) => {
											setConnectionId(e.target.value);
											setReport(null);
										}}
									>
										<option value="">Selecione um canal</option>
										{connections.map((item) => (
											<option key={item.id} value={item.id}>
												{item.name}
											</option>
										))}
									</select>
								</label>
							</div>
							<button
								type="button"
								className="btn btn-primary"
								onClick={search}
								disabled={loading || !connectionId}
							>
								{loading ? "Consultando…" : "Buscar"}
							</button>
						</div>
					</div>

					{loading || !report ? <MetricsSkeleton pulse={loading} /> : (
						<>
							<p className="m-0 rounded-xl border border-warning/30 bg-warning/10 px-4 py-3 text-sm text-default-800 dark:text-default-100">
								{report.note}
							</p>

							<div className="grid gap-3 sm:grid-cols-2 xl:grid-cols-4">
								<Stat
									label="Volume total"
									value={formatInt(stats.total)}
									hint="Soma das mensagens que a Meta marcou como entregues no período."
								/>
								<Stat
									label="Mínimo no dia"
									value={formatInt(stats.min)}
									hint="O menor volume que a Meta devolveu em um único dia."
								/>
								<Stat
									label="Máximo no dia"
									value={formatInt(stats.max)}
									hint="O maior volume que a Meta devolveu em um único dia."
								/>
								<Stat
									label="Média diária"
									value={Number(stats.avg || 0).toLocaleString("pt-BR", {
										maximumFractionDigits: 1,
									})}
									hint={`Média dos ${formatInt(stats.days)} dias em que a Meta devolveu volume.`}
								/>
							</div>

							<div className="card">
								<div className="card-body space-y-4">
									<SectionIntro
										title="Volume por dia"
										text="Cada linha é uma categoria da Meta. O número é de mensagens entregues naquele dia, não de conversas abertas no painel."
									/>
									<div className="h-72">
										<ResponsiveContainer width="100%" height="100%">
											<LineChart data={series}>
												<CartesianGrid strokeDasharray="3 3" stroke={gridColor} />
												<XAxis dataKey="date" stroke={axisColor} tick={{ fill: axisColor, fontSize: 12 }} />
												<YAxis allowDecimals={false} stroke={axisColor} tick={{ fill: axisColor, fontSize: 12 }} />
												<Tooltip content={<ChartTooltip darkMode={darkMode} />} />
												<Legend wrapperStyle={{ color: axisColor }} />
												{Object.keys(CATEGORY_COLOR).map((key) => (
													<Line
														key={key}
														type="monotone"
														dataKey={key}
														name={labelOf(key)}
														stroke={CATEGORY_COLOR[key]}
														dot={false}
														strokeWidth={2}
													/>
												))}
											</LineChart>
										</ResponsiveContainer>
									</div>
									<div className="grid gap-2 sm:grid-cols-2">
										{CATEGORY_HELP.map((item) => (
											<p key={item.key} className="m-0 text-sm text-default-600 dark:text-default-300">
												<span className="font-semibold text-default-800 dark:text-default-100">
													{labelOf(item.key)}.
												</span>{" "}
												{item.text}
											</p>
										))}
									</div>
								</div>
							</div>

							<div className="card">
								<div className="card-body space-y-3">
									<SectionIntro
										title="Estimativa pela tabela da Meta (Brasil)"
										text="Preço base é o valor de referência de uma mensagem entregue. Total estimado é o volume vezes esse preço. Não é a fatura: a Meta pode cobrar mais ou menos por país, faixa de volume e mensagens grátis."
									/>
									<p className="m-0 text-sm font-semibold text-default-800 dark:text-default-100">
										Referência do período: {formatBrl(priceTotal)}
									</p>
									<div className="overflow-x-auto">
										<table className="w-full text-sm">
											<thead>
												<tr className="border-b border-default-200 text-left text-default-500 dark:border-default-700">
													<th className="py-2 pr-3 font-medium">Categoria</th>
													<th className="py-2 pr-3 font-medium">Volume</th>
													<th className="py-2 pr-3 font-medium">Preço base</th>
													<th className="py-2 font-medium">Total estimado</th>
												</tr>
											</thead>
											<tbody>
												{prices.map((item) => (
													<tr
														key={item.category}
														className="border-b border-default-100 dark:border-default-800"
													>
														<td className="py-2 pr-3">{labelOf(item.category)}</td>
														<td className="py-2 pr-3">{formatInt(item.volume)}</td>
														<td className="py-2 pr-3">{formatBrl(item.unitBrl)}</td>
														<td className="py-2 font-semibold">
															{formatBrl(item.totalBrl)}
														</td>
													</tr>
												))}
											</tbody>
										</table>
									</div>
									{categories.some(
										(item) => !prices.find((price) => price.category === item.category)
									) ? (
										<p className="m-0 text-xs text-default-500">
											Categorias fora da tabela do Brasil aparecem só no volume.
										</p>
									) : null}
								</div>
							</div>

							<div className="card">
								<div className="card-body space-y-4">
									<SectionIntro
										title="Templates do envio em massa"
										text="A rosca mostra só quem respondeu. Enviados é a lista do disparo com data de envio. Leads é quem tinha o mesmo telefone e mandou qualquer mensagem nos 7 dias seguintes. Isso não prova que a pessoa voltou por causa do template. Retorno é leads dividido pelos enviados."
									/>
									{templateDonut.length ? (
										<div className="h-72">
											<ResponsiveContainer width="100%" height="100%">
												<PieChart>
													<Pie
														data={templateDonut}
														dataKey="value"
														nameKey="name"
														innerRadius={68}
														outerRadius={100}
														paddingAngle={2}
													>
														{templateDonut.map((item, index) => (
															<Cell
																key={item.name}
																fill={DONUT_COLORS[index % DONUT_COLORS.length]}
															/>
														))}
													</Pie>
													<Tooltip content={<ChartTooltip darkMode={darkMode} />} />
													<Legend wrapperStyle={{ color: axisColor }} />
												</PieChart>
											</ResponsiveContainer>
										</div>
									) : (
										<p className="m-0 text-sm text-default-500">
											{templates.length
												? "Nenhuma resposta nesse prazo. Os envios continuam na tabela."
												: "Nenhum template enviado nesse período."}
										</p>
									)}
									<div className="overflow-x-auto">
										<table className="w-full text-sm">
											<thead>
												<tr className="border-b border-default-200 text-left text-default-500 dark:border-default-700">
													<th className="py-2 pr-3 font-medium">Template</th>
													<th className="py-2 pr-3 font-medium">Enviados</th>
													<th className="py-2 pr-3 font-medium">Leads</th>
													<th className="py-2 font-medium">Retorno</th>
												</tr>
											</thead>
											<tbody>
												{templates.map((item) => (
													<tr
														key={`${item.templateId}-${item.name}`}
														className="border-b border-default-100 dark:border-default-800"
													>
														<td className="py-2 pr-3">
															{item.name || item.templateId || "—"}
														</td>
														<td className="py-2 pr-3">{formatInt(item.sent)}</td>
														<td className="py-2 pr-3">{formatInt(item.replied)}</td>
														<td className="py-2">{formatPct(item.replyRate)}</td>
													</tr>
												))}
											</tbody>
										</table>
									</div>
								</div>
							</div>

							<div className="card">
								<div className="card-body space-y-4">
									<SectionIntro
										title="Anúncios da Meta"
										text="Aqui não entra o clique do Gerenciador de Anúncios. Entra só o clique que abriu o WhatsApp: a Meta manda um código único na primeira mensagem. Cada código é um clique. Pessoas são os contatos diferentes que chegaram por aquele anúncio. Se uma pessoa clicar duas vezes, conta dois cliques e uma pessoa."
									/>
									{ads.length ? (
										<>
											<div className="grid gap-3 sm:grid-cols-3">
												<Stat
													label="Cliques no WhatsApp"
													value={formatInt(adClicks)}
													hint="Códigos distintos que abriram uma conversa."
												/>
												<Stat
													label="Pessoas"
													value={formatInt(adPeople)}
													hint="Contatos diferentes que chegaram por anúncio."
												/>
												<Stat
													label="Pessoas por clique"
													value={adClicks ? formatPct(adPeople / adClicks) : "—"}
													hint="Quanto do clique virou uma pessoa diferente."
												/>
											</div>
											<div className="h-72">
												<ResponsiveContainer width="100%" height="100%">
													<BarChart data={adsChart} layout="vertical" margin={{ left: 16, right: 12 }}>
														<CartesianGrid strokeDasharray="3 3" stroke={gridColor} />
														<XAxis type="number" allowDecimals={false} stroke={axisColor} tick={{ fill: axisColor, fontSize: 12 }} />
														<YAxis type="category" dataKey="name" width={140} stroke={axisColor} tick={{ fill: axisColor, fontSize: 12 }} />
														<Tooltip content={<ChartTooltip darkMode={darkMode} />} />
														<Legend wrapperStyle={{ color: axisColor }} />
														<Bar dataKey="Cliques" fill="#d97706" />
														<Bar dataKey="Pessoas" fill="#0284c7" />
													</BarChart>
												</ResponsiveContainer>
											</div>
											<div className="overflow-x-auto">
												<table className="w-full text-sm">
													<thead>
														<tr className="border-b border-default-200 text-left text-default-500 dark:border-default-700">
															<th className="py-2 pr-3 font-medium">Anúncio</th>
															<th className="py-2 pr-3 font-medium">Origem</th>
															<th className="py-2 pr-3 font-medium">ID do anúncio</th>
															<th className="py-2 pr-3 font-medium">Cliques</th>
															<th className="py-2 pr-3 font-medium">Pessoas</th>
															<th className="py-2 font-medium">Pessoas por clique</th>
														</tr>
													</thead>
													<tbody>
														{ads.map((item) => (
															<tr
																key={item.sourceId || item.title}
																className="border-b border-default-100 dark:border-default-800"
															>
																<td className="py-2 pr-3">
																	<div>{item.title || "Sem título"}</div>
																	{item.sourceUrl ? (
																		<a
																			className="text-xs text-primary"
																			href={item.sourceUrl}
																			target="_blank"
																			rel="noreferrer"
																		>
																			abrir anúncio
																		</a>
																	) : null}
																</td>
																<td className="py-2 pr-3">{adOrigin(item)}</td>
																<td className="py-2 pr-3 font-mono text-xs">
																	{item.sourceId || "—"}
																</td>
																<td className="py-2 pr-3">{formatInt(item.clicks)}</td>
																<td className="py-2 pr-3">{formatInt(item.contacts)}</td>
																<td className="py-2">{formatPct(item.rate)}</td>
															</tr>
														))}
													</tbody>
												</table>
											</div>
										</>
									) : (
										<p className="m-0 text-sm text-default-500">
											Nenhuma conversa aberta por anúncio nesse período. O clique só aparece depois que a pessoa manda a primeira mensagem.
										</p>
									)}
								</div>
							</div>

							{channels.some((item) => item.error) ? (
								<div className="rounded-xl border border-danger/30 bg-danger/10 px-4 py-3 text-sm">
									{channels
										.filter((item) => item.error)
										.map((item) => (
											<p key={item.id} className="m-0">
												{item.name}: {item.error}
											</p>
										))}
								</div>
							) : null}
						</>
					)}
				</div>
			</div>
		</div>
	);
}
